import type { ZodObject, ZodType } from 'zod/v4';

import { handleError } from '../errors/handle-error.js';
import { bindUrlParams } from '../request/bind-url-params.js';
import { makeConfig } from '../request/make-config.js';
import type { ErrorSchemaRecord } from '../types/error-schema.js';
import type {
  AbstractResponse,
  BuilderContext,
  EndpointOptions,
  StreamOptions,
} from '../types/index.js';

/**
 * Base request type for handler functions.
 * Includes urlParams, params (query), data (body), and request options.
 */
export interface HandlerRequest {
  urlParams?: Record<string, string | number>;
  params?: Record<string, unknown>;
  data?: unknown;
  signal?: AbortSignal | null;
  headers?: Record<string, string>;
  [key: string]: unknown;
}

export interface CreateHandlerOptions<
  Options extends EndpointOptions | StreamOptions,
> {
  options: Options;
  context: BuilderContext;
  isMultipart?: boolean;
  responseSchema?: ZodType;
  errorSchema?: ErrorSchemaRecord;
  /** Optional Zod schema for validating URL parameters at runtime */
  urlParamsSchema?: ZodObject;
  transformRequest?: (request: HandlerRequest) => HandlerRequest;
  transformResponse?: (data: unknown) => unknown;
  /**
   * MINT-OVERRIDE: receives the full AbstractResponse<unknown> instead of
   * just `.data`. Used by declareStream when `fullResponse: true` so the
   * handler can return status/statusText/headers alongside the body.
   * If both transformFullResponse and transformResponse are provided,
   * transformFullResponse wins.
   */
  transformFullResponse?: (response: AbstractResponse<unknown>) => unknown;
  /**
   * MINT-OVERRIDE: forwarded to handleError so the discriminator-mode error
   * path can wrap the parsed error in AbstractResponse shape when set.
   */
  fullResponse?: boolean;
}

export function createHandler<
  Options extends EndpointOptions | StreamOptions,
  TResponse,
>({
  options,
  context: { getClient, config },
  isMultipart = false,
  responseSchema,
  errorSchema,
  urlParamsSchema,
  transformRequest,
  transformResponse,
  transformFullResponse, // MINT-OVERRIDE
  fullResponse, // MINT-OVERRIDE
}: CreateHandlerOptions<Options>) {
  const { method, url } = options;

  const handler = async (
    request: HandlerRequest = {} as HandlerRequest,
  ): Promise<TResponse> => {
    const client = getClient();
    const finalUrlPart = bindUrlParams<Options['url']>(
      url,
      request,
      urlParamsSchema,
    );
    const finalRequest = transformRequest ? transformRequest(request) : request;
    // MINT-OVERRIDE: hoist makeConfig result so we can forward the real axios
    // request config to handleError (so onZodError receives it as 4th arg)
    const requestConfig = makeConfig(
      finalRequest,
      options,
      method,
      finalUrlPart,
      isMultipart,
    );

    try {
      const result = await client.request(requestConfig);

      // MINT-OVERRIDE: transformFullResponse takes precedence over transformResponse
      let data: unknown;
      if (transformFullResponse) {
        data = transformFullResponse(result);
      } else if (transformResponse) {
        data = transformResponse(result.data);
      } else {
        data = result.data;
      }

      return (responseSchema ? responseSchema.parse(data) : data) as TResponse;
    } catch (error) {
      // handleError may return a parsed response (when useDiscriminatorResponse is true)
      // or throw an error
      // MINT-OVERRIDE: forward requestConfig to handleError so onZodError receives it as 4th arg
      return handleError(
        config,
        error,
        responseSchema,
        errorSchema,
        requestConfig,
        fullResponse, // MINT-OVERRIDE: forward so discriminator-mode errors can be wrapped
      ) as TResponse;
    }
  };

  handler.config = options;

  return handler;
}
